/**
 * Tenant Statement Modal Mixin
 *
 * Shared state and methods for the tenancy "Generate statement" modal, used
 * by both tenancySlideover and tenancyAccountSlideover. Spread it into the
 * component alongside the other mixins; it expects `tenancyId` on the host.
 *
 * The default is the whole tenancy — the server works out the account's
 * real extent, which often starts before the tenancy does (the deposit
 * lands first). The other presets and the custom range send explicit dates.
 */

function isoDate(date) {
  return date.toISOString().slice(0, 10)
}

function monthsAgo(months) {
  const date = new Date()
  date.setMonth(date.getMonth() - months)
  return isoDate(date)
}

export function tenantStatementModalMixin() {
  return {
    // State
    statementModalOpen: false,
    statementRange: 'whole',
    statementFromDate: '',
    statementToDate: '',
    statementGenerating: false,

    tenantStatementPresets() {
      const today = isoDate(new Date())
      return [
        { key: 'whole', label: 'Whole tenancy', from: '', to: '' },
        { key: 'last12', label: 'Last 12 months', from: monthsAgo(12), to: today },
        { key: 'last6', label: 'Last 6 months', from: monthsAgo(6), to: today },
        { key: 'last3', label: 'Last 3 months', from: monthsAgo(3), to: today },
      ]
    },

    openStatementModal() {
      this.selectStatementPreset(this.tenantStatementPresets()[0])
      this.statementGenerating = false
      this.statementModalOpen = true
    },

    closeStatementModal() {
      this.statementModalOpen = false
      this.statementGenerating = false
    },

    selectStatementPreset(preset) {
      this.statementRange = preset.key
      this.statementFromDate = preset.from
      this.statementToDate = preset.to
    },

    // Editing either date makes the range custom; the chips simply stop matching.
    statementDatesEdited() {
      this.statementRange = 'custom'
    },

    canGenerateStatement() {
      if (!this.tenancyId || this.statementGenerating) return false
      if (this.statementRange === 'whole') return true
      return Boolean(this.statementFromDate && this.statementToDate)
    },

    statementUrl(format = 'pdf') {
      const url = `/accounting/accounts/tenancies/${this.tenancyId}/statement.${format}`
      if (this.statementRange === 'whole') return url
      return `${url}?from_date=${this.statementFromDate}&to_date=${this.statementToDate}`
    },

    generateStatement(format = 'pdf') {
      if (!this.canGenerateStatement()) return

      this.statementGenerating = true
      window.open(this.statementUrl(format), '_blank')

      // Reset after a short delay (PDF opens in new tab)
      setTimeout(() => {
        this.statementGenerating = false
        this.statementModalOpen = false
      }, 1000)
    }
  }
}
